<template>
    <rkm-form
        v-if="model"
        ref="RkmForm"
    >
        <rkm-area title="Dados da competência">
            <rkm-row>
                <rkm-col cols="8">
                    <rkm-form-select
                        :options="form.unidades"
                        :validations="validations.unidadeId"
                        label="Unidade"
                        show-default-option
                        text-field="nomeFantasia"
                        v-model="model.unidadeId"
                        value-field="id"
                    />
                </rkm-col>

                <rkm-col cols="2">
                    <rkm-form-select
                        :options="form.competencias"
                        :validations="validations.competenciaId"
                        @change="reset"
                        label="Mês"
                        show-default-option
                        text-field="mes"
                        v-model="model.competenciaId"
                        value-field="id"
                    />
                </rkm-col>

                <rkm-col cols="2">
                    <rkm-form-select
                        :options="form.competencias"
                        :validations="validations.competenciaId"
                        @change="reset"
                        label="Ano"
                        show-default-option
                        text-field="ano"
                        v-model="model.competenciaId"
                        value-field="id"
                        disabled
                    />
                </rkm-col>
            </rkm-row>
        </rkm-area>

        <rkm-area title="Dados do procedimento">
            <rkm-row>
                <rkm-col cols="8">
                    <rkm-form-input-autocomplete
                        :disabled="!model.competenciaId"
                        :repository="repositories.procedimento"
                        :request-query-string-params="{
                            competencia_id: model.competenciaId,
                            instrumento_registro: enums.registro.BPA_CONSOLIDADO,
                            search_by_apelido: this.additionals.searchByApelido,
                        }"
                        :search-fields="fields.procedimento"
                        :text-field="(item) => `${formatProcedimento(item.codigo)} - ${item.nome}`"
                        :validations="validations.procedimentoId"
                        label="Procedimento"
                        v-model="model.procedimento"
                    >
                        <rkm-input-group-append slot="append">
                            <rkm-input-group-text>
                                <rkm-form-radio-group v-model="additionals.searchByApelido">
                                    <rkm-form-radio :value="false">Código / Nome</rkm-form-radio>
                                    <rkm-form-radio :value="true">Apelido</rkm-form-radio>
                                </rkm-form-radio-group>
                            </rkm-input-group-text>
                        </rkm-input-group-append>
                    </rkm-form-input-autocomplete>
                </rkm-col>

                <rkm-col cols="4">
                    <rkm-form-input-autocomplete
                        :disabled="!model.procedimento"
                        :repository="repositories.ocupacao"
                        :request-query-string-params="{ procedimento_id: model.procedimento ? model.procedimento.id : null }"
                        :search-fields="fields.ocupacao"
                        :text-field="(item) => `${item.codigo} - ${item.nome}`"
                        :validations="validations.ocupacaoId"
                        label="CBO"
                        placeholder="Digite o código ou nome do CBO"
                        v-model="model.ocupacao"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="4">
                    <rkm-form-input
                        :disabled="!model.procedimento"
                        :validations="validations.quantidade"
                        label="Quantidade"
                        type="number"
                        v-model="model.quantidade"
                    />
                </rkm-col>

                <rkm-col cols="4">
                    <rkm-form-input
                        :disabled="disabledIdade || !model.procedimento"
                        :validations="validations.idade"
                        label="Idade"
                        type="number"
                        v-model="model.idade"
                    />
                </rkm-col>
            </rkm-row>
        </rkm-area>
    </rkm-form>
</template>

<script>
    // Enums.
    import registro from '@/enums/sigtap/registro'

// Mixins.
import { error, formats, repository, stringCase } from '@/mixins'

// Vuex.
import { mapActions } from 'vuex'

import mesesAno from '@/enums/geral/mesesAno'

export default {
    computed: {
        fields() {
            return {
                procedimento: this.additionals.searchByApelido
                    ? [{ key: 'parametrizacao_procedimento.apelido', searchable: true }]
                    : [{ key: 'codigo', searchable: true }, { key: 'nome', searchable: true }],
                ocupacao: [
                    { key: 'codigo', searchable: true },
                    { key: 'nome', searchable: true },
                ],
            }
        },
        validations() {
            return {
                unidadeId: { required: true },
                competenciaId: { required: true },
                procedimentoId: { required: true },
                ocupacaoId: { required: true },
                quantidade: {
                    required: true,
                    min_value: 1,
                    max_value: 999999,
                },
                idade: {
                    min_value: 0,
                    max_value: 130,
                    required: this.model.procedimento && this.model.procedimento.exigeIdadeBpaConsolidado ? true : false,
                },
            }
        },
    },
    created() {
        this.repositories = {
            procedimentoConsolidado: this.getConectaSusRepository('bpa.procedimentoConsolidado'),
            ocupacao: this.getConectaSusRepository('sigtap.ocupacao'),
            procedimento: this.getConectaSusRepository('sigtap.procedimento'),
        }
    },
    data: () => ({
        additionals: {
            searchByApelido: false,
        },
        enums: {
            registro,
            mesesAno
        },
        form: null,
        model: null,
        disabledIdade: false,
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {
            this.setLoading()

            if (this.id) {
                const request = {
                    acao: 'lancamento',
                    origem: this.origem
                }

                this.repositories.procedimentoConsolidado.edit(this.id, this.camelObjectKeysToSnakeRecursive(request))
                    .then(response => {
                        this.form = Object.assign(response.data.form)
                        this.form.competencias = this.form.competencias.map(c => {
                            return {
                                id: c.id,
                                mes: this.enums.mesesAno[parseInt(c.valor.slice(4))],
                                ano: c.valor.slice(0,4)
                            }
                        })
                        this.model = Object.assign(response.data.model)
                        this.disabledIdade = this.model.idade == null
                    })
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())
            } else {
                const request = { acao: 'lancamento' }

                this.repositories.procedimentoConsolidado.create(this.camelObjectKeysToSnakeRecursive(request))
                    .then(response => {
                        this.form = Object.assign(response.data.form)
                        this.form.competencias = this.form.competencias.map(c => {
                            return {
                                id: c.id,
                                mes: this.enums.mesesAno[parseInt(c.valor.slice(4))],
                                ano: c.valor.slice(0,4)
                            }
                        })
                        this.model = Object.assign(response.data.model)
                    })
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())
            }
        },
        save() {
            this.$refs.RkmForm.validate()
                .then(valid => {
                    if (valid) {
                        this.setLoading()

                        let request = {
                            acao: this.model.acao,
                            unidadeId: this.model.unidadeId,
                            competenciaId: this.model.competenciaId,
                            procedimentoId: this.model.procedimento.id,
                            ocupacaoId: this.model.ocupacao.id,
                            quantidade: this.model.quantidade,
                            idade: this.model.idade,
                        }

                        if (this.id) {
                            request.origem = this.origem

                            this.repositories.procedimentoConsolidado.update(this.id, this.camelObjectKeysToSnakeRecursive(request))
                                .then(response => {
                                    this.showSaveNotification()
                                    this.$emit('save', response)
                                })
                                .catch(this.handleError)
                                .finally(() => this.unsetLoading())
                        } else {
                            this.repositories.procedimentoConsolidado.store(this.camelObjectKeysToSnakeRecursive(request))
                                .then(response => {
                                    this.showSaveNotification()
                                    this.$emit('save', response)
                                })
                                .catch(this.handleError)
                                .finally(() => this.unsetLoading())
                        }
                    }
                })
        },
        reset() {
            this.model.procedimento = null
            this.model.ocupacao = null
            this.model.quantidade = null
            this.model.idade = null
        },
    },
    mixins: [ error, formats, repository, stringCase ],
    mounted() {
        this.load()
    },
    props: {
        id: { type: [Number, String], default: null },
        origem: { type: String },
    },
}
</script>